module AppComponent where

import Prelude

import App (HereaboutEnv)
import Clerk (User, getUser, mountSignIn, mountUserButton, openSignIn, openUserProfile, signOut, userImageUrl, userUserName)
import Control.Monad.Reader (class MonadAsk, ask, asks)
import Control.Monad.Rec.Class (forever)
import Data.Array as A
import Data.Either (Either(..))
import Data.Maybe (Maybe(..), fromJust)
import Effect.Aff (Milliseconds(..), delay)
import Effect.Aff as Aff
import Effect.Aff.Class (class MonadAff)
import Effect.Class (class MonadEffect, liftEffect)
import Effect.Class.Console (log)
import Halogen (liftAff)
import Halogen as H
import Halogen.HTML (p)
import Halogen.HTML as HH
import Halogen.HTML.Events as HE
import Halogen.HTML.Properties as HP
import Halogen.Subscription as HS
import Hereabout.Data.Route (CommunityFeedTab, HereaboutPage(..), Route(..), routeCodec)
import Hereabout.Data.Route as HR
import Hereabout.Effects (class Favourite, class LocationFeed, class Navigate, class RenderExternalUserComponents, class ResolveRoute, class SharePost, class ShareURL, class UserSession, navigate, resolveRoute)
import Hereabout.HTML.RawContent as RawHTML
import Hereabout.Navigator.Geolocation (GeolocationError(..), Position(..), PositionMissing(..), accuracy, watchCurrentPosition)
import Hereabout.Types (AreaCommunityDetails(..), CommunityFeed(..), Event(..), EventWithComments, EventWithComments(..), GeoPosition(..), Venue(..), VenueFeed(..))
import Hereabout.UI.Bulma.Classes as Bulma
import Hereabout.UI.Components.CommunityFeedComponent as CommunityFeedComp
import Hereabout.UI.Components.CommunityListComponent as CommunityListComp
import Hereabout.UI.Components.EventComponent as EventComp
import Hereabout.UI.Components.GPSFeedComponent as GPSFeedComp
import Hereabout.UI.Components.RawHTMLComponent as RawHTMLComp
import Hereabout.UI.Components.UserProfileComponent as UserProfileComp
import Hereabout.UI.Components.VenueFeedComponent as VenueFeedComp
import Hereabout.UI.SemanticUI.Classes as SemUI
import Partial.Unsafe (unsafePartial)
import Routing.Duplex (print)
import Type.Equality (class TypeEquals, from)
import Type.Proxy (Proxy(..))
import Web.Event.Event (preventDefault)
import Web.Event.Internal.Types as WE
import Web.HTML as HTML
import Web.HTML.Event.EventTypes (offline)
import Web.HTML.HTMLDocument as HTMLDocument
import Web.HTML.Window as Window
import Web.UIEvent.MouseEvent as ME

data Action 
  = InitComponent
  | NavigateToRoute WE.Event Route
  | ToggleMenu
  | OpenSignIn
  | OpenUserProfile
  | SignOut

data Query a = 
  ChangeLocationHash Route a

type Slot id = forall q. H.Slot q Void id

type ChildSlots =
  ( cfc :: CommunityFeedComp.Slot { id :: String, tab :: CommunityFeedTab}
  , vfc :: VenueFeedComp.Slot String
  , gpsfc :: GPSFeedComp.Slot Unit
  , acdc :: CommunityListComp.Slot Unit
  , ec :: EventComp.Slot String
  , rawc :: RawHTMLComp.Slot String
  , upc :: UserProfileComp.Slot Unit
  )

type State =
  { credentials :: Maybe String
  , authCode :: Maybe String
  , position :: Either PositionMissing Position
  , page :: HereaboutPage
  , user :: Maybe User
  , showMenu :: Boolean
  }

data CommunityFeedSelectedData
  = EventCFData
  | VenueCFData

userButtonRef ∷ H.RefLabel
userButtonRef = H.RefLabel "user-button"

signInButtonRef ∷ H.RefLabel
signInButtonRef = H.RefLabel "sign-in-button"

component :: forall o m. MonadAsk HereaboutEnv m => MonadAff m => MonadEffect m => Navigate m => LocationFeed m => ResolveRoute m => ShareURL m => UserSession m => SharePost m => RenderExternalUserComponents m => Favourite m => H.Component Query Unit o m
component =
  H.mkComponent
    { initialState: \unit -> { credentials : Nothing, authCode : Nothing, position : Left AwaitingPosition, page : EmptyPage, user : Nothing, showMenu : false }
    , render
    , eval: H.mkEval H.defaultEval { handleAction = handleAction, handleQuery = handleQuery, initialize = Just InitComponent }
    }

renderNav :: forall m. State -> H.ComponentHTML Action ChildSlots m
renderNav state =
  HH.nav [ HP.classes [ Bulma.navbar, Bulma.isPrimary ] ]
    [ HH.div [ HP.classes [ Bulma.navbarBrand ] ]
      [ HH.a [ HP.href (print routeCodec GPSFeedRoute), HE.onClick \me -> NavigateToRoute (ME.toEvent me) GPSFeedRoute ]
        [ HH.img [ HP.classes [ Bulma.navbarItem ], HP.src "/hereabout.svg", HP.height 32, HP.width 280]
        ]
      , HH.a [ HP.href "javascript:void(0)", HP.classes $ [ Bulma.navbarBurger ] <> extraMenuClasses, HE.onClick \_ -> ToggleMenu ] 
        [ HH.span_ []
        , HH.span_ []
        , HH.span_ []
        , HH.span_ []
        ]
      ]
    , HH.div [ HP.classes $ [ Bulma.navbarMenu ] <> extraMenuClasses  ]
      [ HH.div [ HP.classes [ Bulma.navbarEnd ] ]
        [ renderDropdown state
        ]
      ]
    ]
    where
      extraMenuClasses =
        case state.showMenu of
          true  -> [ Bulma.isActive ]
          false -> []

renderDropdown :: forall m. State -> H.ComponentHTML Action ChildSlots m
renderDropdown state =
  HH.div [ HP.classes [ Bulma.navbarItem, Bulma.hasDropdown, Bulma.isHoverable ] ]
    [ HH.a [ HP.classes [ Bulma.navbarLink, Bulma.isSize5Desktop ] ]
      [ HH.text "Menu"
      ]
    , HH.div [ HP.classes [ Bulma.navbarDropdown, Bulma.isBoxed, Bulma.isRight, Bulma.hasBackgroundPrimary ], HP.style "width:400px"  ]
      [ HH.div [ HP.classes $ [ Bulma.navbarItem, Bulma.isSelected ] <> extraUserButtonClasses ]
          case state.user of
            Just user ->
              [ HH.div [ HP.classes [ Bulma.field, Bulma.hasAddons ] ]
                [ HH.p [ HP.classes [ Bulma.control ] ]
                  [ HH.figure [ HP.classes [ Bulma.image, Bulma.is32x32, Bulma.mr3 ] ]
                    [ HH.img [ HP.classes [ Bulma.isRounded ], HP.src $ userImageUrl user  ]
                    ]
                  ]
                , HH.p [ HP.classes [ Bulma.control, Bulma.isSize5Desktop ] ] 
                  [ HH.text $ userUserName user ]
                ]
              ]
            Nothing -> []
      , HH.div [ HP.classes $ [ Bulma.navbarItem, Bulma.isSelected, Bulma.isSize5Desktop  ] <> extraUserButtonClasses ]
        case state.user of
          Just _ ->
            [ HH.a [ HE.onClick \me -> NavigateToRoute (ME.toEvent me) UserProfileRoute   ] 
              [ HH.text "My Profile"
              ]
            ]
          Nothing -> []
      , HH.div [ HP.classes $ [ Bulma.navbarItem, Bulma.isSelected, Bulma.isSize5Desktop ] ]
        [ HH.a [ HE.onClick \me -> NavigateToRoute (ME.toEvent me) CommunityListRoute ] 
          [ HH.text "Communities"
          ]
        ]
      , HH.div [ HP.classes $ [ Bulma.navbarItem, Bulma.isSelected, Bulma.isSize5Desktop  ] <> extraUserButtonClasses ]
        case state.user of
          Just _ ->
            [ HH.a [ HE.onClick \_ -> SignOut ] 
              [ HH.text "Sign Out"
              ]
            ]
          Nothing -> []
      , HH.div [ HP.classes $ [ Bulma.navbarItem, Bulma.isSelected, Bulma.isSize5Desktop   ] <> extraSigninClasses ]
        [ HH.a [ HE.onClick \_ -> OpenSignIn ] 
          [ HH.text "Sign in"
          ]
        ]
      ]
    ]
  where 
    extraUserButtonClasses = 
      case state.user of 
        Just u -> []
        Nothing -> [ Bulma.isHidden ]
    extraSigninClasses = 
      case state.user of 
        Just u -> [ Bulma.isHidden ]
        Nothing -> []


render :: forall m. MonadAff m => LocationFeed m => Navigate m => ShareURL m => UserSession m => SharePost m => RenderExternalUserComponents m => Favourite m => State -> H.ComponentHTML Action ChildSlots m
render state =
  HH.div [ ]
    [ renderNav state
    , HH.div [ HP.classes [ H.ClassName "container", H.ClassName "ui", Bulma.hero, Bulma.isFullHeight ] ]
      [ case state.page of
          CommunityFeedPage (CommunityFeed cf) tab -> 
            HH.slot_ (Proxy :: _ "cfc") {id : cf.id, tab : tab} CommunityFeedComp.component { communityFeed : CommunityFeed cf, cfTab : tab }
          VenuePage (VenueFeed vf) -> 
            HH.slot_ (Proxy :: _ "vfc") (vf.id) VenueFeedComp.component (VenueFeed vf)
          GPSFeedPage -> 
            HH.slot_ (Proxy :: _ "gpsfc") unit GPSFeedComp.component unit
          CommunityListPage acds -> 
            HH.slot_ (Proxy :: _ "acdc") unit CommunityListComp.component acds
          EventPage (EventWithComments evt) ->
            HH.slot (Proxy :: _ "ec") (evt.id) EventComp.component (EventWithComments evt) handleEventComponentOutput
          TermsOfServicePage ->
            HH.slot_ (Proxy :: _ "rawc") "terms" RawHTMLComp.component { html : RawHTML.termsOfServiceHtml }
          PrivacyPolicyPage ->
            HH.slot_ (Proxy :: _ "rawc") "privacy" RawHTMLComp.component { html : RawHTML.privacyPolicyHtml }
          UserProfilePage ->
            HH.slot_ (Proxy :: _ "upc") unit UserProfileComp.component {}
          EmptyPage ->
            HH.div_ []
      ]
    , HH.footer [ HP.classes [ H.ClassName "footer" ] ]
      [ HH.div [ HP.classes [ Bulma.content, H.ClassName "has-text-centered"] ]
        [ HH.text "Copyright © 2024 Purestack Limited. All rights reserved. | "
        , HH.a [ HE.onClick \me -> NavigateToRoute (ME.toEvent me) PrivacyPolicyRoute ] 
          [ HH.text "Privacy Policy"
          ]
        , HH.text " | "
        , HH.a [ HE.onClick \me -> NavigateToRoute (ME.toEvent me) TermsOfServiceRoute ] 
          [ HH.text "Terms of Service"
          ]
        ]
      ]
    ]

handleEventComponentOutput ∷ EventComp.Output -> Action
handleEventComponentOutput EventComp.RequestSignIn = OpenSignIn

handleQuery :: forall o m. MonadAff m => ResolveRoute m => forall a. Query a -> H.HalogenM State Action ChildSlots o m (Maybe a)
handleQuery (ChangeLocationHash dest a) = do
  dest' <- H.lift $ resolveRoute dest
  H.modify_ _ { page = dest' }
  H.liftEffect $ HTML.window >>= Window.document >>= HTMLDocument.setTitle (HR.title dest')
  pure $ Just a
  
handleAction :: forall cs o m. MonadAsk HereaboutEnv m => MonadAff m => MonadEffect m => Navigate m => Action -> H.HalogenM State Action cs o m Unit
handleAction InitComponent = do
  userButtonComp <- H.getHTMLElementRef $ userButtonRef
  inst <- H.lift $ asks (\e -> e.clerkInstance)
  maybeUser <- H.liftEffect $ getUser inst

  case {comp : userButtonComp, u : maybeUser} of
    {comp : Just userButton, u : Just user} -> do
      H.liftEffect $ mountUserButton userButton inst
      H.modify_ _ { user = Just user }
    {comp : Nothing, u : Just user} -> do
      H.modify_ _ { user = Just user }
    _ -> pure unit
  pure unit
handleAction (NavigateToRoute e route) = do
  H.liftEffect $ preventDefault e
  H.lift $ navigate route
handleAction ToggleMenu =
  H.modify_ (\s -> s { showMenu = not s.showMenu })
handleAction OpenSignIn = do
  inst <- H.lift $ asks (\e -> e.clerkInstance)
  H.liftEffect $ openSignIn inst
handleAction OpenUserProfile = do
  inst <- H.lift $ asks (\e -> e.clerkInstance)
  H.liftEffect $ openUserProfile inst
handleAction SignOut = do
  inst <- H.lift $ asks (\e -> e.clerkInstance)
  H.liftAff $ signOut inst




